import React from 'react'
import { Check, Loader2, X } from 'lucide-react'
import Button from '../../../../../components/ui/button'
import type { ScriptSuggestionChange } from '../../../../../types/script-refinement'
import { DiffPreview } from './diff-preview'
import clsx from 'clsx'

export type LineSuggestionCardProps = {
  suggestion: ScriptSuggestionChange
  isActive: boolean
  onAccept: (suggestion: ScriptSuggestionChange) => void
  onReject: (suggestion: ScriptSuggestionChange) => void
  onFocus: (suggestion: ScriptSuggestionChange) => void
  disabled?: boolean
  isBusy?: boolean
}

export const LineSuggestionCard: React.FC<LineSuggestionCardProps> = ({
  suggestion,
  isActive,
  onAccept,
  onReject,
  onFocus,
  disabled,
  isBusy,
}) => {
  const isDeletion = suggestion.kind === 'delete'
  const title = isDeletion ? 'Suggested removal' : 'Suggested edit'

  const handleFocus = () => {
    if (disabled) return
    onFocus(suggestion)
  }

  const handleAccept = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (disabled) return
    onAccept(suggestion)
  }

  const handleReject = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (disabled) return
    onReject(suggestion)
  }

  return (
    <div
      className={clsx(
        'mt-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5 transition hover:ring-black/10',
        'cursor-pointer relative',
        isActive && 'ring-2 !ring-brand-500 ring-offset-2',
        disabled && 'opacity-60 cursor-not-allowed'
      )}
      onClick={handleFocus}
      role="group"
      data-suggestion-id={suggestion.id}
    >
      {/* Left accent by type */}
      <span
        aria-hidden="true"
        className={clsx(
          'absolute left-0 top-0 h-full w-1 rounded-l-xl',
          isDeletion ? 'bg-danger-400' : 'bg-brand-400'
        )}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-black/[0.04] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 ring-1 ring-black/10">
            {title}
          </span>
          {suggestion.originalSpeaker && suggestion.proposedSpeaker && suggestion.originalSpeaker !== suggestion.proposedSpeaker && (
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-50 px-2 py-0.5 text-[11px] font-medium text-secondary-700 ring-1 ring-secondary-200">Host: {suggestion.originalSpeaker} → {suggestion.proposedSpeaker}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleReject}
            disabled={disabled || isBusy}
            className="cursor-pointer text-foreground/70 hover:text-foreground hover:bg-black/[0.04]"
          >
            <X className="h-4 w-4" />
            <span>Reject</span>
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleAccept}
            disabled={disabled || isBusy}
            className="cursor-pointer"
          >
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}<span>Accept</span>
          </Button>
        </div>
      </div>
      <div className="mt-3 space-y-2 text-sm leading-relaxed text-foreground/75">
        {isDeletion ? (
          <p className="text-foreground/65">This line will be removed.</p>
        ) : (
          <DiffPreview segments={suggestion.diff} />
        )}
      </div>
    </div>
  )
}
